"use client";

import React from "react";
import { Calendar, ChevronLeft, Tag, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";


declare const wvcClient: any;

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="animate-pulse space-y-8">
        <div className="h-8 w-32 rounded-md bg-muted"></div>
        <div className="space-y-4">
          <div className="h-12 w-3/4 rounded-md bg-muted"></div>
          <div className="h-12 w-1/2 rounded-md bg-muted"></div>
        </div>
        <div className="flex gap-4">
          <div className="h-6 w-24 rounded-md bg-muted"></div>
          <div className="h-6 w-24 rounded-md bg-muted"></div>
        </div>
        <div className="h-[400px] w-full rounded-xl bg-muted"></div>
        <div className="space-y-4">
          <div className="h-4 w-full rounded-md bg-muted"></div>
          <div className="h-4 w-full rounded-md bg-muted"></div>
          <div className="h-4 w-5/6 rounded-md bg-muted"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="mb-4 text-lg text-destructive">Failed to load the post.</p>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h2 className="font-default mb-4 text-2xl font-bold">Post Not Found</h2>
        <p className="mb-8 text-muted-foreground">The article you are looking for does not exist or has been removed.</p>
        <Link to="/HomePage">
          <Button variant="default">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="mx-auto max-w-4xl">
      {}
      <div className="mb-8">
        <Link to="/HomePage">
          <Button variant="ghost" className="pl-0 text-muted-foreground hover:text-foreground">
            <ChevronLeft className="mr-2 size-4" />
            Back to Home
          </Button>
        </Link>
      </div>

      {}
      <header className="mb-10">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {post.categories.map((category, i) => (
              <Link key={category.id} to={category.link || "#"} data-index={i}>
                <span className="inline-flex items-center rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  {category.name}
                </span>
              </Link>
            ))}
          </div>
        )}

        <h1 className="font-default mb-6 text-4xl font-bold leading-tight tracking-tight md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              {post.author.avatar && post.author.avatar["48"] ? (
                <img
                  src={post.author.avatar["48"]}
                  alt={post.author.name}
                  className="size-8 rounded-lg object-cover"
                />
              ) : (
                <div className="flex size-8 items-center justify-center rounded-lg bg-muted">
                  <User className="size-4" />
                </div>
              )}
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}

          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="size-4" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 overflow-hidden rounded-xl bg-muted shadow-lg">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
            loading="eager"
          />
        </div>
      )}

      {}
      <div
        className="font-default prose-invert mb-16 max-w-none text-lg leading-relaxed text-foreground/90 [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>h2]:mb-6 [&>h2]:mt-12 [&>h2]:text-3xl [&>h2]:font-bold [&>h3]:mb-4 [&>h3]:mt-8 [&>h3]:text-2xl [&>h3]:font-bold [&>h4]:mb-4 [&>h4]:mt-6 [&>h4]:text-xl [&>h4]:font-semibold [&>img]:my-8 [&>img]:rounded-xl [&>ol]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>p]:mb-6 [&>ul]:mb-6 [&>ul]:list-disc [&>ul]:pl-6"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mb-12 flex flex-wrap items-center gap-3 border-t border-border pt-8">
          <Tag className="size-5 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Tags:</span>
          {post.tags.map((tag, i) => (
            <Link key={tag.id} to={tag.link || "#"} data-index={i}>
              <span className="inline-flex items-center rounded-md border border-border bg-background px-3 py-1 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      {}
      {post.author && (
        <Card className="overflow-hidden border-border bg-card/50 shadow-md backdrop-blur-sm">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col items-start gap-6 sm:flex-row sm:items-center">
              {post.author.avatar && post.author.avatar["96"] ? (
                <img
                  src={post.author.avatar["96"]}
                  alt={post.author.name}
                  className="size-20 shrink-0 rounded-xl object-cover shadow-sm"
                />
              ) : (
                <div className="flex size-20 shrink-0 items-center justify-center rounded-xl bg-muted shadow-sm">
                  <User className="size-8 text-muted-foreground" />
                </div>
              )}
              <div className="flex-1">
                <h3 className="font-default mb-2 text-xl font-bold">
                  Written by {post.author.name}
                </h3>
                <p className="mb-4 text-muted-foreground">
                  Pool maintenance expert at Rocket City Water Wizards. Dedicated to providing technical clarity and professional care for your aquatic systems.
                </p>
                {post.author.link && (
                  <Link to={post.author.link}>
                    <Button variant="outline" size="sm">
                      View all posts by {post.author.name}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

export default function Post() {
  
  const currentWPQueryParams = typeof window !== "undefined" && wvcClient 
    ? wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post" };
    
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="22002"
      id="single-post-template-section"
      className="bg-background text-foreground py-16 md:py-24 lg:py-32"
    >
      <div className="container mx-auto px-4">
        <PostsProvider wp_query={wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}